import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { DocsLinkChip } from '../common/DocsLinkChip';
import { useDocsLinks } from '../../hooks/useDocsLinks';
import { CalibrePickerModal } from './CalibrePickerModal';
import toast from 'react-hot-toast';
import {
  appendCalibreTier,
  copyCalibreImagesToCalibre,
  migrateStandaloneToTier,
  type CustomCalibreEntry,
  type CalibreData,
  type CalibreTier,
  type CalibreTierOverrides,
  type MigrateStandaloneToTierResult,
} from '../../services/firebase';
import {
  OVERRIDABLE_FIELDS,
  baseValueFor,
  countOverrides,
  displayOverrideValue,
  isAccuracyKey,
  resolveTierSuggestion,
  sanitizeTierForWrite,
  tiersForBrand,
  applyTierCarryOver,
  sourceCalibreImages,
  tierManufacturersFromSource,
} from './calibreTiers';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';

/** Which tier the source becomes: one already on the target, or a fresh one. */
type TierChoice = { kind: 'new' } | { kind: 'existing'; tierId: string };

/**
 * Reclassify a STANDALONE calibre into a brand-specific TIER of another (base)
 * calibre. A guided wizard:
 *
 *   1. Pick the target base calibre (calibre picker).
 *   2. Choose the brand (defaults from the source calibre's brand).
 *   3. Pick the tier: either an EXISTING tier on the target (listed with its
 *      overrides), or "create new" with a name and a general/brand-specific
 *      type. The list is scoped by `tiersForBrand`, so it shows the target's
 *      general grades plus this brand's own modifications and never another
 *      brand's.
 *   4. Preview the deltas. Creating a tier: source spec vs target base → the new
 *      tier's overrides. Merging into an existing tier: source spec vs that
 *      tier's EFFECTIVE spec, accepted per field, so the migration can enrich a
 *      tier the curator (or the AI) already built rather than only reusing it.
 *   5. Preview downstream impact + confirm → creates the tier (if new) and runs
 *      the `migrateStandaloneToTier` callable, which re-points refs, adds the
 *      source name as a brand-scoped AKA, and deprecates the source.
 *
 * The migration is destructive-ish (pointer rewrites + a soft-delete), so the
 * final step is explicitly gated behind a typed confirm.
 */
export function ReclassifyCalibreWizard({
  isOpen,
  source,
  brandOptions,
  onClose,
  onComplete,
}: {
  isOpen: boolean;
  /** The standalone calibre being reclassified. */
  source: CustomCalibreEntry;
  brandOptions: Array<{ id: string; name: string }>;
  onClose: () => void;
  /** Called after a successful real migration (parent should refresh + close). */
  onComplete: (result: MigrateStandaloneToTierResult) => void;
}) {
  const [step, setStep] = useState(1);
  const [target, setTarget] = useState<CustomCalibreEntry | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [brandId, setBrandId] = useState<string>(source.brandId ?? '');
  const [brandSearch, setBrandSearch] = useState('');
  const [tierName, setTierName] = useState('');
  // The new tier's description. Starts as the source calibre's own — that
  // calibre is about to be deprecated, and its prose would otherwise go with it.
  const [tierDescription, setTierDescription] = useState(source.description ?? '');
  /**
   * Which tier the source becomes. `existing` merges into a tier already on the
   * target; `new` mints one. Held as a discriminated union rather than a
   * nullable id so "create new" is a deliberate choice the user can return to,
   * not the absence of a selection.
   */
  const [tierChoice, setTierChoice] = useState<TierChoice>({ kind: 'new' });
  const [tierType, setTierType] = useState<'general' | 'brand-specific'>(
    'brand-specific',
  );
  /**
   * Whether the user has touched the type toggle. The default is derived from
   * the target's manufacturer, which is only known once a target is picked (and
   * once the catalogue loads) — without this flag, that late-arriving default
   * would overwrite a choice the user had already made.
   */
  const [tierTypeTouched, setTierTypeTouched] = useState(false);
  /** Per-field accept state for a merge into an existing tier, keyed by override key. */
  const [acceptedMergeKeys, setAcceptedMergeKeys] = useState<Record<string, boolean>>({});
  const [impact, setImpact] = useState<MigrateStandaloneToTierResult | null>(
    null,
  );
  const [impactLoading, setImpactLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');

  // Reset everything whenever the wizard (re)opens for a source.
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setTarget(null);
    setPickerOpen(false);
    setBrandId(source.brandId ?? '');
    setBrandSearch('');
    setTierName('');
    setTierDescription(source.description ?? '');
    setTierChoice({ kind: 'new' });
    setTierType('brand-specific');
    setTierTypeTouched(false);
    setAcceptedMergeKeys({});
    setImpact(null);
    setRunning(false);
    setError(null);
    setConfirmText('');
  }, [isOpen, source.id, source.brandId, source.description]);

  // The source calibre's manufacturer owns the naming conventions this step
  // has to respect. Fetched by id: the wizard is handed a calibre entry, not a
  // manufacturer document.
  const { docs: sourceManufacturerDocs } = useDocsLinks(
    'manufacturer',
    source.manufacturerId,
  );

  // Memoized, not derived inline: these feed `mergeDeltas`, which in turn seeds
  // the per-field accept state through an effect. A fresh `{}` on every render
  // (the case whenever `data` is absent — including before a target is picked)
  // would make that memo recompute every render and the effect set state every
  // render, which is an infinite loop, not a wasted cycle.
  const sourceSpec = useMemo<CalibreData>(() => source.data ?? {}, [source]);
  const targetSpec = useMemo<CalibreData>(() => target?.data ?? {}, [target]);

  const filteredBrands = useMemo(() => {
    const term = brandSearch.trim().toLowerCase();
    if (!term) return brandOptions;
    return brandOptions.filter(
      (b) =>
        b.name.toLowerCase().includes(term) ||
        b.id.toLowerCase().includes(term),
    );
  }, [brandOptions, brandSearch]);

  /**
   * The target's tiers this brand may legitimately use: every general grade plus
   * this brand's own modifications. Deliberately NOT the raw `tiers[]` — a
   * shared base movement carries a brand-specific tier for each brand that
   * modifies it, and offering one of those here would attach this brand's
   * references to a foreign brand's tier.
   *
   * This is also the fix for the case that prompted the picker: general tiers
   * (the ones AI enrichment creates on a fresh base) were invisible to the old
   * brand-specific-only filter, so there was nothing to reuse and every
   * migration minted a duplicate.
   */
  const selectableTiers = useMemo(
    () => tiersForBrand(target?.data?.tiers, brandId),
    [target, brandId],
  );

  const selectedTier = useMemo(
    () =>
      tierChoice.kind === 'existing'
        ? selectableTiers.find((t) => t.id === tierChoice.tierId) ?? null
        : null,
    [tierChoice, selectableTiers],
  );

  // A typed name that already exists is reused rather than duplicated — the
  // behaviour that predates the picker, kept because the name field is still
  // reachable and a duplicate tier name is never what anyone wants.
  const exactNameMatch = useMemo(
    () =>
      selectableTiers.find(
        (t) => t.name.trim().toLowerCase() === tierName.trim().toLowerCase(),
      ) ?? null,
    [selectableTiers, tierName],
  );

  /**
   * A typed name that is not an exact match but resolves to an existing tier
   * anyway — "Elabore" for "Élaboré", "chronometre" for "Chronometer".
   * Surfaced as a suggestion the user can accept, never applied silently:
   * `resolveTierSuggestion` is accent- and alias-aware and refuses to answer
   * when more than one tier fits, but it is still a fuzzy match.
   */
  const nearNameMatch = useMemo(() => {
    if (tierChoice.kind !== 'new') return null;
    if (!tierName.trim() || exactNameMatch) return null;
    const id = resolveTierSuggestion(selectableTiers, undefined, tierName.trim());
    return id ? selectableTiers.find((t) => t.id === id) ?? null : null;
  }, [tierChoice.kind, tierName, exactNameMatch, selectableTiers]);

  // The TARGET's manufacturer decides the default tier type: a generic house
  // (ETA, Sellita, Miyota) sells one movement to many brands, so a new tier
  // there is a brand's own modification and must be scoped; a sole-brand house
  // (Ball, NOMOS, Rolex) has no other brand to leak to, and scoping its tiers is
  // what made them invisible to `tiersForBrand` and so to AI enrichment.
  const { manufacturers: manufacturerCatalogue } = useManufacturerCatalogue({
    enabled: isOpen,
  });
  const targetIsGeneric = useMemo(() => {
    const id = target?.manufacturerId?.trim();
    if (!id) return undefined;
    return manufacturerCatalogue.find((m) => m.id === id)?.isGeneric;
  }, [manufacturerCatalogue, target]);
  // `undefined` (manufacturer unclassified, unknown, or absent) keeps the
  // pre-existing brand-specific default — the safe end, since a brand-specific
  // tier can be widened later by "Convert to general" but a general tier already
  // applied across brands cannot be narrowed without knowing which refs meant
  // which.
  const defaultTierType: 'general' | 'brand-specific' =
    targetIsGeneric === false ? 'general' : 'brand-specific';

  useEffect(() => {
    if (tierTypeTouched) return;
    setTierType(defaultTierType);
  }, [defaultTierType, tierTypeTouched]);

  // A tier id only means something against the target it came from, and the
  // brand decides which tiers are on offer at all — so changing either drops the
  // selection rather than carrying a now-invalid one forward.
  useEffect(() => {
    setTierChoice({ kind: 'new' });
    setAcceptedMergeKeys({});
  }, [target?.id, brandId]);

  // Deltas: overridable fields where the source diverges from the target base.
  // These become the new tier's overrides.
  const deltas = useMemo(() => {
    return OVERRIDABLE_FIELDS.filter((f) => !isAccuracyKey(f.key))
      .map((f) => {
        const sourceVal = baseValueFor(sourceSpec, f.key);
        const targetVal = baseValueFor(targetSpec, f.key);
        return { key: f.key, label: f.label, sourceVal, targetVal };
      })
      .filter((d) => normalize(d.sourceVal) !== normalize(d.targetVal));
  }, [sourceSpec, targetSpec]);

  /**
   * Merge mode: the fields where the source diverges from the tier's EFFECTIVE
   * spec (its overrides laid over the target base). Comparing against the base
   * alone would list fields the tier already resolves, and the table would
   * describe changes that aren't changes.
   *
   * `conflict` marks a field the tier ALREADY overrides to a different value —
   * accepting it replaces curated data, so it defaults to unchecked while a
   * plain addition defaults to checked. A source value of `undefined` is never
   * offered: "the source doesn't say" is not a reason to clear a tier override.
   */
  const mergeDeltas = useMemo(() => {
    if (!selectedTier) return [];
    return OVERRIDABLE_FIELDS.filter((f) => !isAccuracyKey(f.key))
      .map((f) => {
        const tierOverride = selectedTier.overrides[f.key];
        const conflict = tierOverride !== undefined;
        return {
          key: f.key,
          label: f.label,
          sourceVal: baseValueFor(sourceSpec, f.key),
          effectiveVal: conflict ? tierOverride : baseValueFor(targetSpec, f.key),
          conflict,
        };
      })
      .filter(
        (d) =>
          d.sourceVal !== undefined &&
          normalize(d.sourceVal) !== normalize(d.effectiveVal),
      );
  }, [selectedTier, sourceSpec, targetSpec]);

  // Seed the accept state whenever the offered deltas change: additions on,
  // conflicts off.
  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const d of mergeDeltas) next[d.key] = !d.conflict;
    setAcceptedMergeKeys(next);
  }, [mergeDeltas]);

  const acceptedMergeDeltas = useMemo(
    () => mergeDeltas.filter((d) => acceptedMergeKeys[d.key]),
    [mergeDeltas, acceptedMergeKeys],
  );

  const mergedOverrides = useMemo(() => {
    if (!selectedTier) return null;
    const o: CalibreTierOverrides = { ...selectedTier.overrides };
    for (const d of acceptedMergeDeltas) {
      (o as Record<string, unknown>)[d.key] = d.sourceVal;
    }
    return o;
  }, [selectedTier, acceptedMergeDeltas]);

  /**
   * Merging into a GENERAL tier writes overrides that every brand using this
   * movement will read. Harmless on a sole-brand manufacturer — the case this
   * whole flow is built around — and a real hazard on a generic one, where the
   * source's modifications would silently become ETA's or Sellita's spec.
   */
  const genericGeneralMergeWarning =
    !!selectedTier &&
    selectedTier.tierType !== 'brand-specific' &&
    targetIsGeneric === true &&
    acceptedMergeDeltas.length > 0;

  const overridesFromDeltas = useMemo(() => {
    const o: CalibreTierOverrides = {};
    for (const d of deltas) {
      if (d.sourceVal !== undefined) {
        // d.key is a keyof CalibreTierOverrides and d.sourceVal is that field's
        // value read from the source spec — the assignment is sound; the cast
        // just bridges the per-key union that TS can't narrow across the loop.
        (o as Record<string, unknown>)[d.key] = d.sourceVal;
      }
    }
    return o;
  }, [deltas]);

  const brandName =
    brandOptions.find((b) => b.id === brandId)?.name ?? brandId;

  const canPickBrand = !!brandId;
  // Either an existing tier is selected, or a name has been typed for a new one.
  const canNameTier =
    tierChoice.kind === 'existing' ? !!selectedTier : tierName.trim().length > 0;

  /** The tier this migration will point references at, however it was chosen. */
  const resolvedTier = selectedTier ?? exactNameMatch;
  /** Display name for the chosen tier — the summary and step 5 both read it. */
  const effectiveTierName = resolvedTier?.name ?? tierName.trim();

  // Load the downstream impact when entering step 5 — a dry-run of the migration
  // callable, so the preview counts match reality exactly (id-linked AND
  // legacy-string refs, brand-scoped, with the same guards). The tier may not
  // exist yet (created on confirm), so a blank tierId is fine on a dry run.
  useEffect(() => {
    if (step !== 5 || !target) return;
    let cancelled = false;
    setImpactLoading(true);
    migrateStandaloneToTier({
      sourceCalibreId: source.id,
      targetCalibreId: target.id,
      targetTierId: resolvedTier?.id ?? '',
      brandId,
      dryRun: true,
    })
      .then((res) => {
        if (!cancelled) setImpact(res);
      })
      .catch(() => {
        if (!cancelled) setImpact(null);
      })
      .finally(() => {
        if (!cancelled) setImpactLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, target, source.id, brandId, resolvedTier]);

  // The dry-run is authoritative: it refuses multi-brand itself, and also echoes
  // the brand span for a heads-up before confirm.
  const multiBrand =
    impact?.refused?.reason === 'multi-brand-refs' ||
    (impact?.refBrandIds.length ?? 0) > 1;
  const multiTierSource = (source.data?.tiers?.length ?? 0) > 1;

  // What the source hands to its tier besides overrides: ALL its manufacturers
  // (primary + additional — e.g. valjoux + benzinger) and a COPY of every image.
  // Both used to be dropped silently (Confluence 2284224513).
  const carriedManufacturers = useMemo(
    () => tierManufacturersFromSource(source, target),
    [source, target],
  );
  const carriedImages = useMemo(() => sourceCalibreImages(source), [source]);
  const carriedManufacturerNames = [
    carriedManufacturers.manufacturerId,
    ...(carriedManufacturers.additionalManufacturerIds ?? []),
  ].filter(Boolean) as string[];

  const handleConfirm = async () => {
    if (!target) return;
    setRunning(true);
    setError(null);
    try {
      // 1. Ensure the target carries the tier this migration points at. Three
      //    routes, all landing on a tier id:
      //      - an existing tier was selected → reuse it, and write back the
      //        accepted deltas when there are any (appendCalibreTier upserts by
      //        id, so this updates in place rather than appending);
      //      - a typed name matched an existing tier → reuse it untouched, as
      //        the wizard has always done;
      //      - otherwise mint a new tier.
      //
      //    Every write goes through sanitizeTierForWrite. That is not tidiness:
      //    a general tier carries `brandId: undefined`, and Firestore is
      //    initialised without `ignoreUndefinedProperties`, so writing the raw
      //    object would throw.
      // 0. Copy the source's images onto the TARGET's Storage prefix (copy, not
      //    move: the source keeps its own in case the reclassify is reverted).
      const { images: copiedImages, failed: imagesFailed } =
        carriedImages.length > 0
          ? await copyCalibreImagesToCalibre(carriedImages, target.id)
          : { images: [], failed: 0 };
      const hasCarryOver = carriedManufacturerNames.length > 0 || copiedImages.length > 0;
      const withCarryOver = (tier: CalibreTier) =>
        sanitizeTierForWrite(applyTierCarryOver(tier, carriedManufacturers, copiedImages));

      let tierId: string;
      if (selectedTier && mergedOverrides) {
        tierId = selectedTier.id;
        if (acceptedMergeDeltas.length > 0 || hasCarryOver) {
          await appendCalibreTier(target.id, withCarryOver({ ...selectedTier, overrides: mergedOverrides }));
        }
      } else if (exactNameMatch) {
        tierId = exactNameMatch.id;
        if (hasCarryOver) await appendCalibreTier(target.id, withCarryOver(exactNameMatch));
      } else {
        const newTier: CalibreTier = withCarryOver({
          // Stable tier id — crypto.randomUUID is available in all supported
          // browsers (matches CalibreTiersEditor.newTierId).
          id: crypto.randomUUID(),
          name: tierName.trim(),
          // Omitted when blank: an empty description is not a value worth storing.
          ...(tierDescription.trim() ? { description: tierDescription.trim() } : {}),
          tierType,
          brandId,
          overrides: overridesFromDeltas,
        });
        tierId = await appendCalibreTier(target.id, newTier);
      }

      // 2. Run the migration for real.
      const result = await migrateStandaloneToTier({
        sourceCalibreId: source.id,
        targetCalibreId: target.id,
        targetTierId: tierId,
        brandId,
        dryRun: false,
      });

      if (result.refused) {
        setError(result.refused.message);
        setRunning(false);
        return;
      }
      if (imagesFailed > 0) {
        toast.error(
          `${imagesFailed} image${imagesFailed === 1 ? '' : 's'} could not be copied to the tier — they are still on “${source.name}”.`,
          { duration: 8000 },
        );
      }
      onComplete(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Migration failed — see console.',
      );
      console.error('migrateStandaloneToTier failed:', err);
    } finally {
      setRunning(false);
    }
  };

  if (!isOpen) return null;

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Reclassify “${source.name}” as a tier`}
      size='xl'
    >
      <div className='space-y-4'>
        <StepHeader step={step} />

        {/* Step 1 — pick target base calibre */}
        {step === 1 && (
          <div className='space-y-3'>
            <p className='text-sm text-gray-600'>
              Pick the <span className='font-medium'>base calibre</span> this
              standalone should become a tier of (e.g. the Sellita SW200 that
              “{source.name}” modifies).
            </p>
            <div className='flex items-center gap-2'>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => setPickerOpen(true)}
              >
                {target ? 'Change target…' : 'Pick target calibre…'}
              </Button>
              {target && (
                <span className='text-sm text-gray-800'>
                  Target:{' '}
                  <span className='font-medium'>{target.name}</span>{' '}
                  <span className='font-mono text-xs text-gray-400'>
                    ({target.id})
                  </span>
                </span>
              )}
            </div>
            {target && target.id === source.id && (
              <p className='text-xs text-red-600'>
                Target must be a different calibre.
              </p>
            )}
          </div>
        )}

        {/* Step 2 — choose brand */}
        {step === 2 && (
          <div className='space-y-2'>
            <p className='text-sm text-gray-600'>
              Which brand owns this modification? Defaults to the source
              calibre’s brand.
            </p>
            <input
              type='text'
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder='Search brands…'
              className={inputClass}
            />
            <select
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              className={inputClass}
            >
              <option value=''>
                {brandOptions.length === 0
                  ? 'No brands loaded'
                  : `Select a brand… (${filteredBrands.length} match)`}
              </option>
              {filteredBrands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Step 3 — tier name */}
        {step === 3 && (
          <div className='space-y-2'>
            <p className='text-sm text-gray-600'>
              Pick the tier “{source.name}” becomes on {target?.name} — an
              existing one available to{' '}
              <span className='font-medium'>{brandName}</span>, or a new one.
            </p>
            {/* The most taxonomy-sensitive step in the admin: a tier name is
                what every later import matches against, and the suffix rules
                that decide it live in Confluence. Surface them HERE rather than
                trusting the curator to remember they exist. */}
            {sourceManufacturerDocs.length > 0 && (
              <div className='flex items-center gap-2 flex-wrap rounded-md border border-blue-100 bg-blue-50/60 px-2 py-1.5'>
                <span className='text-xs text-blue-800'>
                  Check the known suffix conventions before naming this tier:
                </span>
                <DocsLinkChip
                  links={sourceManufacturerDocs}
                  entityLabel={source.manufacturerId ?? undefined}
                  hint='Suffix and naming conventions for this manufacturer.'
                />
              </div>
            )}
            <TierPicker
              tiers={selectableTiers}
              choice={tierChoice}
              onChoose={setTierChoice}
              targetName={target?.name}
              brandName={brandName}
            />

            {tierChoice.kind === 'new' && (
              <div className='space-y-2 rounded-lg border border-gray-200 bg-white p-3'>
                <input
                  type='text'
                  value={tierName}
                  onChange={(e) => setTierName(e.target.value)}
                  placeholder={`e.g., ${brandName} mod`}
                  aria-label='New tier name'
                  className={inputClass}
                  autoFocus
                />

                <div>
                  <label className='block text-xs font-medium text-gray-600 mb-1'>
                    Tier type
                  </label>
                  <div className='inline-flex rounded-lg border border-gray-300 overflow-hidden'>
                    <button
                      type='button'
                      onClick={() => {
                        setTierTypeTouched(true);
                        setTierType('general');
                      }}
                      aria-pressed={tierType === 'general'}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        tierType === 'general'
                          ? 'bg-brand-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      General
                    </button>
                    <button
                      type='button'
                      onClick={() => {
                        setTierTypeTouched(true);
                        setTierType('brand-specific');
                      }}
                      aria-pressed={tierType === 'brand-specific'}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 ${
                        tierType === 'brand-specific'
                          ? 'bg-brand-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Brand-specific
                    </button>
                  </div>
                  <p className='text-[11px] text-gray-400 mt-1'>
                    {tierType === 'general'
                      ? `A manufacturer grade on ${target?.name ?? 'the target'} — visible to every brand using it, and to AI enrichment.`
                      : `Scoped to ${brandName}: only that brand’s references can use it.`}
                  </p>
                  {!tierTypeTouched && targetIsGeneric !== undefined && (
                    <p className='text-[11px] text-gray-400'>
                      Defaulted from the target’s manufacturer, which is{' '}
                      {targetIsGeneric
                        ? 'a generic movement house (many brands use it).'
                        : 'not a generic movement house (no other brand to scope against).'}
                    </p>
                  )}
                </div>

                {exactNameMatch ? (
                  <p className='text-xs text-emerald-600'>
                    Reuses the existing tier “{exactNameMatch.name}” on the target
                    — no new tier created. Select it above instead if you also
                    want the source’s values merged into it.
                  </p>
                ) : nearNameMatch ? (
                  <p className='text-xs text-amber-700'>
                    “{tierName.trim()}” looks like the existing tier “
                    {nearNameMatch.name}”.{' '}
                    <button
                      type='button'
                      onClick={() =>
                        setTierChoice({ kind: 'existing', tierId: nearNameMatch.id })
                      }
                      className='underline font-medium'
                    >
                      Use “{nearNameMatch.name}” instead
                    </button>
                  </p>
                ) : (
                  tierName.trim() && (
                    <p className='text-xs text-gray-500'>
                      Creates a new {tierType === 'general' ? 'general' : 'brand-specific'}{' '}
                      tier on {target?.name}.
                    </p>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 4 — preview deltas (merge into an existing tier, or the new
            tier's overrides) */}
        {step === 4 && selectedTier && (
          <div className='space-y-2'>
            <p className='text-sm text-gray-600'>
              These values differ between “{source.name}” and the effective spec
              of tier{' '}
              <span className='font-medium'>{selectedTier.name}</span> (its
              overrides over the {target?.name} base). Tick the ones the tier
              should adopt.
            </p>
            {mergeDeltas.length === 0 ? (
              <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500'>
                Nothing to merge — “{source.name}” already matches this tier’s
                effective spec. The migration re-points its references and
                changes no values.
              </div>
            ) : (
              <>
                <div className='border rounded-lg overflow-hidden'>
                  <table className='w-full text-xs'>
                    <thead className='bg-gray-50 text-gray-500'>
                      <tr>
                        <th className='w-10 px-3 py-1.5' />
                        <th className='text-left font-medium px-3 py-1.5'>Field</th>
                        <th className='text-left font-medium px-3 py-1.5'>
                          Tier now
                        </th>
                        <th className='text-left font-medium px-3 py-1.5'>
                          From {source.name}
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-100'>
                      {mergeDeltas.map((d) => (
                        <tr key={d.key}>
                          <td className='px-3 py-1.5'>
                            <input
                              type='checkbox'
                              checked={!!acceptedMergeKeys[d.key]}
                              aria-label={`Merge ${d.label}`}
                              onChange={(e) =>
                                setAcceptedMergeKeys((prev) => ({
                                  ...prev,
                                  [d.key]: e.target.checked,
                                }))
                              }
                              className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                            />
                          </td>
                          <td className='px-3 py-1.5 text-gray-700'>
                            {d.label}
                            {d.conflict && (
                              <span className='ml-1 text-[10px] text-amber-600'>
                                (replaces an override)
                              </span>
                            )}
                          </td>
                          <td className='px-3 py-1.5 text-gray-400'>
                            {displayOverrideValue(d.effectiveVal)}
                          </td>
                          <td className='px-3 py-1.5 font-medium text-gray-900'>
                            {displayOverrideValue(d.sourceVal)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className='text-xs text-gray-500'>
                  {acceptedMergeDeltas.length} of {mergeDeltas.length} will be
                  written to the tier. Fields already overridden by the tier start
                  unticked — accepting one replaces curated data.
                </p>
                {genericGeneralMergeWarning && (
                  <div className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'>
                    “{selectedTier.name}” is a GENERAL tier on a generic movement
                    house. Values merged here apply to every brand using{' '}
                    {target?.name}, not just {brandName}. Pick a brand-specific
                    tier, or untick these fields, unless the change really is the
                    manufacturer’s.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {step === 4 && !selectedTier && (
          <div className='space-y-2'>
            <p className='text-sm text-gray-600'>
              These fields differ between “{source.name}” and the base{' '}
              {target?.name}. They become the tier’s overrides.
            </p>
            {exactNameMatch ? (
              <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800'>
                Reusing an existing tier — its current overrides are kept as-is;
                the deltas below are shown for reference only. Go back and select
                “{exactNameMatch.name}” from the list to merge these values into
                it instead.
              </div>
            ) : deltas.length === 0 ? (
              <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500'>
                No overridable-field differences — the tier will simply mark
                these refs as {brandName}’s variant with no spec overrides.
              </div>
            ) : (
              <div className='border rounded-lg overflow-hidden'>
                <table className='w-full text-xs'>
                  <thead className='bg-gray-50 text-gray-500'>
                    <tr>
                      <th className='text-left font-medium px-3 py-1.5'>
                        Field
                      </th>
                      <th className='text-left font-medium px-3 py-1.5'>
                        Base ({target?.name})
                      </th>
                      <th className='text-left font-medium px-3 py-1.5'>
                        Override (from {source.name})
                      </th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100'>
                    {deltas.map((d) => (
                      <tr key={d.key}>
                        <td className='px-3 py-1.5 text-gray-700'>{d.label}</td>
                        <td className='px-3 py-1.5 text-gray-400'>
                          {displayOverrideValue(d.targetVal)}
                        </td>
                        <td className='px-3 py-1.5 font-medium text-gray-900'>
                          {displayOverrideValue(d.sourceVal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Only when a tier is being minted: an existing tier keeps the
                description it already has. */}
            {!exactNameMatch && (
              <div className='pt-2'>
                <label
                  htmlFor='reclassify-tier-description'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Tier description
                </label>
                <textarea
                  id='reclassify-tier-description'
                  value={tierDescription}
                  onChange={(e) => setTierDescription(e.target.value)}
                  className={inputClass}
                  rows={4}
                  placeholder='What sets this tier apart from the base calibre…'
                />
                <p className='mt-1 text-xs text-gray-500'>
                  {source.description?.trim()
                    ? `Starts as “${source.name}”’s description — edit it before confirming. Saved on the new tier.`
                    : `“${source.name}” has no description. Saved on the new tier; leave blank for none.`}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 5 — impact + confirm */}
        {step === 5 && (
          <div className='space-y-3'>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-1'>
              <div>
                <span className='text-gray-500'>Source: </span>
                <span className='font-medium'>{source.name}</span> → deprecated
                (permanent redirect alias)
                {impact?.sourceRenamedTo && (
                  <>
                    , renamed{' '}
                    <span className='font-medium'>{impact.sourceRenamedTo}</span>
                  </>
                )}
              </div>
              <div>
                <span className='text-gray-500'>Target: </span>
                <span className='font-medium'>{target?.name}</span> · tier{' '}
                <span className='font-medium'>{effectiveTierName}</span> ·{' '}
                {brandName}
              </div>
              <div className='text-xs text-gray-500'>
                {selectedTier
                  ? acceptedMergeDeltas.length > 0
                    ? `Merging ${acceptedMergeDeltas.length} value${acceptedMergeDeltas.length === 1 ? '' : 's'} into the existing tier.`
                    : 'Existing tier reused — no override values changed.'
                  : exactNameMatch
                    ? 'Existing tier reused — no override values changed.'
                    : `Creating a new ${tierType === 'general' ? 'general' : 'brand-specific'} tier with ${Object.keys(overridesFromDeltas).length} override${Object.keys(overridesFromDeltas).length === 1 ? '' : 's'}.`}
              </div>
              {(carriedManufacturerNames.length > 0 || carriedImages.length > 0) && (
                <div className='text-xs text-gray-600' data-testid='tier-carry-over'>
                  Carried over to the tier:{' '}
                  {carriedManufacturerNames.length > 0 && (
                    <>
                      manufacturer{carriedManufacturerNames.length === 1 ? '' : 's'}{' '}
                      <span className='font-medium'>{carriedManufacturerNames.join(', ')}</span>
                    </>
                  )}
                  {carriedManufacturerNames.length > 0 && carriedImages.length > 0 && '; '}
                  {carriedImages.length > 0 &&
                    `${carriedImages.length} image${carriedImages.length === 1 ? '' : 's'} (copied — the source keeps its own)`}
                  .
                </div>
              )}
            </div>

            {impactLoading ? (
              <p className='text-sm text-gray-500'>Calculating impact…</p>
            ) : (
              <>
                <div className='grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm'>
                  <Stat
                    label='Total re-pointed'
                    value={impact?.refsRepointed ?? 0}
                  />
                  <Stat
                    label='id-linked'
                    value={impact?.refsRepointedById ?? 0}
                  />
                  <Stat
                    label='legacy-string'
                    value={impact?.refsRepointedByLegacyString ?? 0}
                  />
                  <Stat
                    label='Owning brands'
                    value={impact?.refBrandIds.length ?? 0}
                    danger={multiBrand}
                  />
                </div>
                <p className='text-sm text-gray-700'>
                  <span className='font-semibold'>
                    {impact?.refsRepointedById ?? 0}
                  </span>{' '}
                  id-linked +{' '}
                  <span className='font-semibold'>
                    {impact?.refsRepointedByLegacyString ?? 0}
                  </span>{' '}
                  legacy-string ref
                  {(impact?.refsRepointed ?? 0) === 1 ? '' : 's'} will be
                  re-pointed to {target?.name} · {effectiveTierName}.
                </p>
                <p className='text-sm text-gray-700'>
                  <span className='font-semibold'>
                    {impact?.akasTransferred ?? 0}
                  </span>{' '}
                  alias{(impact?.akasTransferred ?? 0) === 1 ? '' : 'es'} move to{' '}
                  {target?.name}
                  {(impact?.akaNamesTransferred?.length ?? 0) > 0 && (
                    <>
                      {' '}
                      (
                      <span className='font-mono text-xs'>
                        {impact?.akaNamesTransferred.join(', ')}
                      </span>
                      )
                    </>
                  )}
                  ; the source's own{' '}
                  <span className='font-semibold'>
                    {impact?.sourceAkasStripped ?? 0}
                  </span>{' '}
                  are then cleared.
                </p>
              </>
            )}

            <p className='text-xs text-gray-500'>
              Legacy-string refs are matched case-insensitively on the source
              name + its AKAs, scoped to {brandName}; their{' '}
              <span className='font-mono'>calibre</span> string is preserved as
              provenance. User owned-watches (name-resolved) converge via the
              aliases moved onto the target — which is the ONLY route left, since
              a deprecated calibre no longer matches any string. The source keeps
              a <span className='font-mono'>deprecatedAliasSnapshot</span> of what
              it carried. Cache + Algolia refresh automatically for each
              re-pointed ref.
            </p>

            {multiBrand && (
              <div className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'>
                Refs from {impact?.refBrandIds.length ?? 0} brands (
                {impact?.refBrandIds.join(', ')}) reference this calibre (by id
                or legacy string). A single brand-specific tier can’t represent
                them all — the migration will refuse. Split the calibre first.
              </div>
            )}
            {multiTierSource && (
              <div className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'>
                The source calibre has multiple tiers — the migration will
                refuse. Split it manually first.
              </div>
            )}

            {error && (
              <div className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'>
                {error}
              </div>
            )}

            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Type <span className='font-mono'>RECLASSIFY</span> to confirm
              </label>
              <input
                type='text'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className={inputClass}
                placeholder='RECLASSIFY'
              />
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className='flex justify-between gap-2 border-t border-gray-200 pt-3'>
          <Button
            variant='ghost'
            size='sm'
            onClick={step === 1 ? onClose : () => setStep((s) => s - 1)}
            disabled={running}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          {step < 5 ? (
            <Button
              variant='primary'
              size='sm'
              disabled={!canAdvance(step, {
                target,
                sameAsSource: target?.id === source.id,
                canPickBrand,
                canNameTier,
              })}
              onClick={() => setStep((s) => s + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              variant='primary'
              size='sm'
              loading={running}
              disabled={
                running ||
                confirmText.trim() !== 'RECLASSIFY' ||
                multiBrand ||
                multiTierSource
              }
              onClick={handleConfirm}
            >
              Reclassify &amp; migrate
            </Button>
          )}
        </div>
      </div>

      <CalibrePickerModal
        isOpen={pickerOpen}
        brandName={brandName}
        brandId={brandId || undefined}
        currentCalibreString={source.name}
        onSelect={(entry) => {
          setTarget(entry);
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />
    </Modal>
  );
}

/**
 * The tier list for step 3: every tier available to this brand as a selectable
 * row, plus a "create new" row.
 *
 * Each row states its type and its overrides, because that is what the choice
 * actually turns on — "C" with 4 overrides and "C" with none are different
 * answers to "which tier should these references point at", and the old
 * free-text field showed neither.
 */
function TierPicker({
  tiers,
  choice,
  onChoose,
  targetName,
  brandName,
}: {
  tiers: CalibreTier[];
  choice: TierChoice;
  onChoose: (choice: TierChoice) => void;
  targetName?: string;
  brandName: string;
}) {
  return (
    <div className='space-y-1.5'>
      {tiers.length === 0 ? (
        <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500'>
          {targetName ?? 'The target'} has no tiers available to {brandName} yet
          — create the first one below.
        </div>
      ) : (
        tiers.map((tier) => {
          const selected =
            choice.kind === 'existing' && choice.tierId === tier.id;
          const overrideCount = countOverrides(tier);
          const preview = OVERRIDABLE_FIELDS.filter(
            (f) => tier.overrides[f.key] !== undefined,
          )
            .slice(0, 3)
            .map((f) => `${f.label}: ${displayOverrideValue(tier.overrides[f.key])}`)
            .join(' · ');
          return (
            <button
              key={tier.id}
              type='button'
              onClick={() => onChoose({ kind: 'existing', tierId: tier.id })}
              aria-pressed={selected}
              className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                selected
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <div className='flex flex-wrap items-center gap-2'>
                <span className='text-sm font-medium text-gray-800'>
                  {tier.name}
                </span>
                <span className='text-[11px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5'>
                  {tier.tierType === 'brand-specific'
                    ? 'Brand-specific'
                    : 'General'}
                </span>
                <span className='text-[11px] text-gray-500'>
                  {overrideCount} override{overrideCount === 1 ? '' : 's'}
                </span>
              </div>
              {tier.description && (
                <div className='text-xs text-gray-500'>{tier.description}</div>
              )}
              {preview && (
                <div className='text-[11px] text-gray-400 truncate'>
                  {preview}
                  {overrideCount > 3 ? ' · …' : ''}
                </div>
              )}
            </button>
          );
        })
      )}
      <button
        type='button'
        onClick={() => onChoose({ kind: 'new' })}
        aria-pressed={choice.kind === 'new'}
        className={`w-full text-left rounded-lg border border-dashed px-3 py-2 text-sm font-medium transition-colors ${
          choice.kind === 'new'
            ? 'border-brand-500 bg-brand-50 text-brand-800'
            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        + Create new tier
      </button>
    </div>
  );
}

function canAdvance(
  step: number,
  ctx: {
    target: CustomCalibreEntry | null;
    sameAsSource: boolean;
    canPickBrand: boolean;
    canNameTier: boolean;
  },
): boolean {
  if (step === 1) return !!ctx.target && !ctx.sameAsSource;
  if (step === 2) return ctx.canPickBrand;
  if (step === 3) return ctx.canNameTier;
  return true;
}

const STEP_LABELS = [
  'Target calibre',
  'Brand',
  'Tier',
  'Preview deltas',
  'Impact & confirm',
];

function StepHeader({ step }: { step: number }) {
  return (
    <div className='flex flex-wrap items-center gap-1.5 text-xs'>
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <span
            key={label}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${
              active
                ? 'bg-brand-600 text-white'
                : done
                  ? 'bg-brand-100 text-brand-700'
                  : 'bg-gray-100 text-gray-400'
            }`}
          >
            <span className='font-semibold'>{n}</span>
            <span>{label}</span>
          </span>
        );
      })}
    </div>
  );
}

function Stat({
  label,
  value,
  danger,
}: {
  label: string;
  value: number | string;
  danger?: boolean;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white px-3 py-2'>
      <div className='text-[11px] text-gray-500'>{label}</div>
      <div
        className={`text-lg font-semibold ${danger ? 'text-red-600' : 'text-gray-900'}`}
      >
        {value}
      </div>
    </div>
  );
}

/** Normalize an overridable value for delta comparison (undefined/'' collapse). */
function normalize(v: unknown): string {
  if (v === undefined || v === null || v === '') return '';
  return String(v);
}
